import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Required substring for scripts/validate_dna_blueprints.mjs (runtime writing steps). */
export const DNA_VALIDATE_WRITING_PHRASE = "I ask myself quietly";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Naming the worry",
    opening: localizedGenderNode(
      [
        "Some days there's always something to worry about.",
        "If not this,",
        "then something else.",
        "Something at work.",
        "Something at home.",
        "Something in my body.",
        "As if my mind refuses to rest",
        "until everything is sorted out.",
        "But inside I only grow more tired.",
      ],
      [
        "Some days there's always something to worry about.",
        "If not this,",
        "then something else.",
        "Something at work.",
        "Something at home.",
        "Something in my body.",
        "As if my mind refuses to rest",
        "until everything is sorted out.",
        "But inside I only grow more tired.",
      ]
    ),
    mindfulness: localizedGenderNode(
      ["Pause.", "I'm not solving everything now.", "I'm just noticing that the worry is already here."],
      ["Pause.", "I'm not solving everything now.", "I'm just noticing that the worry is already here."]
    ),
    prompts: localizedGenderNode(
      [
        "What is the worry most alive in me today?",
        "What rises with it in my body?",
        "What sentence keeps replaying inside?",
      ],
      [
        "What is the worry most alive in me today?",
        "What rises with it in my body?",
        "What sentence keeps replaying inside?",
      ]
    ),
    closing: localizedGenderNode(
      ["The worry is here.", "I'm no longer running with it unnoticed."],
      ["The worry is here.", "I'm no longer running with it unnoticed."]
    ),
    action: localizedGenderNode(
      "Today I pause for a moment when I catch myself worrying again.",
      "Today I pause for a moment when I catch myself worrying again."
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "The first trigger",
    opening: localizedGenderNode([
      "Sometimes it starts really small.",
      "A message that didn't come.",
      "Silence.",
      "Something left unresolved.",
      "And I'm already inside it.",
    ]),
    mindfulness: localizedGenderNode([
      "I stay for a moment right at the beginning.",
      "Before my whole mind fills up.",
    ]),
    prompts: localizedGenderNode(
      [
        "What happened right before the worry rose?",
        "What time of day does it kick in fastest?",
        "What am I trying to prevent in that moment?",
      ],
      [
        "What happened right before the worry rose?",
        "What time of day does it kick in fastest?",
        "What am I trying to prevent in that moment?",
      ]
    ),
    closing: localizedGenderNode(
      ["I saw the moment it began.", "That gives me a little space."],
      ["I saw the moment it began.", "That gives me a little space."]
    ),
    action: localizedGenderNode(
      "Today I pay attention to the first moment it ignites.",
      "Today I pay attention to the first moment it ignites."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "Signals from the body",
    opening: localizedGenderNode(
      [
        "Before I even know what I'm worrying about,",
        "my body has already tensed.",
        "Shoulders rising.",
        "Belly tightening.",
        "Breath turning shallow.",
      ],
      [
        "Before I even know what I'm worrying about,",
        "my body has already tensed.",
        "Shoulders rising.",
        "Belly tightening.",
        "Breath turning shallow.",
      ]
    ),
    mindfulness: localizedGenderNode(
      ["I feel where it sits.", "I'm not adding more pressure to my body right now."],
      ["I feel where it sits.", "I'm not adding more pressure to my body right now."]
    ),
    prompts: localizedGenderNode(
      [
        "Where does the worry sit in my body?",
        "What is my body trying to protect?",
        "What could soften a little right now?",
      ],
      [
        "Where does the worry sit in my body?",
        "What is my body trying to protect?",
        "What could soften a little right now?",
      ]
    ),
    closing: localizedGenderNode(
      ["My body holds a lot of this worry.", "Today I'm not ignoring it."],
      ["My body holds a lot of this worry.", "Today I'm not ignoring it."]
    ),
    action: localizedGenderNode(
      "Today I stay a moment with what my body feels, without pushing it away.",
      "Today I stay a moment with what my body feels, without pushing it away."
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "Stories of worry",
    opening: localizedGenderNode([
      "Very quickly the worry becomes a story.",
      "Not just what happened,",
      "but everything that could still happen.",
      "And I'm already reacting to something",
      "that hasn't even happened yet.",
    ]),
    mindfulness: localizedGenderNode([
      "I pause between what is",
      "and everything I've already imagined.",
    ]),
    prompts: localizedGenderNode(
      [
        "What scenario jumps to mind first?",
        "What about it scares me most?",
        "What else could be true here besides that story?",
      ],
      [
        "What scenario jumps to mind first?",
        "What about it scares me most?",
        "What else could be true here besides that story?",
      ]
    ),
    closing: localizedGenderNode(
      ["I saw the story jump in.", "That already slows me a little."],
      ["I saw the story jump in.", "That already slows me a little."]
    ),
    action: localizedGenderNode(
      "Today I tell myself once: not everything has happened yet.",
      "Today I tell myself once: not everything has happened yet."
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "pattern",
    title: "What the worry covers",
    opening: localizedGenderNode([
      "Sometimes the topic shifts,",
      "but the feeling stays.",
      "Today it's money.",
      "Tomorrow, health.",
      "Then someone who didn't reply.",
      "As if the worry just keeps looking for something to latch onto.",
    ]),
    mindfulness: localizedGenderNode([
      "I notice how it changes shape,",
      "but stays the same pressure.",
    ]),
    prompts: localizedGenderNode(
      [
        "What am I worrying about right now?",
        "Which topic comes back most?",
        "What would I rather feel instead of all this?",
      ],
      [
        "What am I worrying about right now?",
        "Which topic comes back most?",
        "What would I rather feel instead of all this?",
      ]
    ),
    closing: localizedGenderNode(
      ["I saw that the worry doesn't really rest.", "It just switches topics."],
      ["I saw that the worry doesn't really rest.", "It just switches topics."]
    ),
    action: localizedGenderNode(
      "Today I write one word for what gripped me strongest.",
      "Today I write one word for what gripped me strongest."
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "fear",
    title: "The loop that returns",
    opening: localizedGenderNode(
      [
        "I have a loop I know well.",
        "Think.",
        "Check.",
        "Go back again.",
        "Try to calm down.",
        "Then think again.",
        "In the end I'm just exhausted.",
      ],
      [
        "I have a loop I know well.",
        "Think.",
        "Check.",
        "Go back again.",
        "Try to calm down.",
        "Then think again.",
        "In the end I'm just exhausted.",
      ]
    ),
    mindfulness: localizedGenderNode([
      "I see this loop while it's happening.",
      "Not only afterward.",
    ]),
    prompts: localizedGenderNode(
      [
        "What's the loop that repeats for me again and again?",
        "How does it usually start?",
        "How do I feel after it passes?",
      ],
      [
        "What's the loop that repeats for me again and again?",
        "How does it usually start?",
        "How do I feel after it passes?",
      ]
    ),
    closing: localizedGenderNode(
      ["It's a loop.", "Not all of who I am."],
      ["It's a loop.", "Not all of who I am."]
    ),
    action: localizedGenderNode(
      "Today I tell myself once: I'm inside the loop.",
      "Today I tell myself once: I'm inside the loop."
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "fear",
    title: "The need beneath",
    opening: localizedGenderNode(
      [
        "Beneath all this worry",
        "there's also something I'm asking for.",
        "To feel safe.",
        "To feel that someone is holding things.",
        "To feel that not everything will fall apart",
        "if I let go for a moment.",
      ],
      [
        "Beneath all this worry",
        "there's also something I'm asking for.",
        "To feel safe.",
        "To feel that someone is holding things.",
        "To feel that not everything will fall apart",
        "if I let go for a moment.",
      ]
    ),
    mindfulness: localizedGenderNode(
      ["I listen to what I really need right now."],
      ["I listen to what I really need right now."]
    ),
    prompts: localizedGenderNode(
      [
        "What am I really looking for beneath this worry?",
        "What am I trying to protect?",
        "How does it feel when I have a little safety?",
      ],
      [
        "What am I really looking for beneath this worry?",
        "What am I trying to protect?",
        "How does it feel when I have a little safety?",
      ]
    ),
    closing: localizedGenderNode(
      ["I have a real need for safety.", "I don't have to fight it."],
      ["I have a real need for safety.", "I don't have to fight it."]
    ),
    action: localizedGenderNode(
      "Today I give myself one moment of steadiness without solving everything.",
      "Today I give myself one moment of steadiness without solving everything."
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "need",
    title: "Being with uncertainty",
    opening: localizedGenderNode(
      [
        "The hardest part is not knowing.",
        "Not knowing if it will work out.",
        "Not knowing if it will happen.",
        "Not knowing if I'm missing something.",
        "And then my mind runs",
        "just to avoid feeling that.",
      ],
      [
        "The hardest part is not knowing.",
        "Not knowing if it will work out.",
        "Not knowing if it will happen.",
        "Not knowing if I'm missing something.",
        "And then my mind runs",
        "just to avoid feeling that.",
      ]
    ),
    mindfulness: localizedGenderNode([
      "I stay a moment with what's not settled.",
      "Just one moment.",
    ]),
    prompts: localizedGenderNode(
      [
        "What feels most uncertain to me right now?",
        "What's hard for me to bear in that?",
        "What gentle sentence could help me hold on for a moment?",
      ],
      [
        "What feels most uncertain to me right now?",
        "What's hard for me to bear in that?",
        "What gentle sentence could help me hold on for a moment?",
      ]
    ),
    closing: localizedGenderNode(
      ["Uncertainty is still hard for me.", "But maybe I don't have to run from it right away."],
      ["Uncertainty is still hard for me.", "But maybe I don't have to run from it right away."]
    ),
    action: localizedGenderNode(
      "Today I pause a moment before I run to find certainty.",
      "Today I pause a moment before I run to find certainty."
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "need",
    title: "A softer inner voice",
    opening: localizedGenderNode(
      [
        "When I worry,",
        "I also start speaking to myself harshly.",
        "As if pushing myself hard enough",
        "will make me calm down.",
        "But inside it only tightens me more.",
      ],
      [
        "When I worry,",
        "I also start speaking to myself harshly.",
        "As if pushing myself hard enough",
        "will make me calm down.",
        "But inside it only tightens me more.",
      ]
    ),
    mindfulness: localizedGenderNode(
      ["I hear how I talk to myself in these moments."],
      ["I hear how I talk to myself in these moments."]
    ),
    prompts: localizedGenderNode(
      [
        "How do I talk to myself when I worry?",
        "What would I want to hear instead?",
        "What softer sentence do I want to try?",
      ],
      [
        "How do I talk to myself when I worry?",
        "What would I want to hear instead?",
        "What softer sentence do I want to try?",
      ]
    ),
    closing: localizedGenderNode(
      ["My tone changes everything.", "I want to remember that."],
      ["My tone changes everything.", "I want to remember that."]
    ),
    action: localizedGenderNode(
      "Today I tell myself one sentence a little softer.",
      "Today I tell myself one sentence a little softer."
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "perspective",
    title: "Setting it down for a moment",
    opening: localizedGenderNode(
      [
        "Maybe I don't have to hold",
        "all this worry in my hands right now.",
        "Maybe I can set it down for a moment.",
        "Not forever.",
        "Just a moment.",
      ],
      [
        "Maybe I don't have to hold",
        "all this worry in my hands right now.",
        "Maybe I can set it down for a moment.",
        "Not forever.",
        "Just a moment.",
      ]
    ),
    mindfulness: localizedGenderNode(
      ["I give myself a small moment without holding everything."],
      ["I give myself a small moment without holding everything."]
    ),
    prompts: localizedGenderNode(
      [
        "What happens in me when I set the worry down for a moment?",
        "What frightens me about setting it down?",
        "What soothes me even a little?",
      ],
      [
        "What happens in me when I set the worry down for a moment?",
        "What frightens me about setting it down?",
        "What soothes me even a little?",
      ]
    ),
    closing: localizedGenderNode(
      ["I set it down for a moment.", "The world didn't collapse from that."],
      ["I set it down for a moment.", "The world didn't collapse from that."]
    ),
    action: localizedGenderNode(
      "Today I give myself one moment without continuing the thought.",
      "Today I give myself one moment without continuing the thought."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "perspective",
    title: "Back to the present",
    opening: localizedGenderNode([
      "The worry always pulls me forward.",
      "To what might happen soon.",
      "To what could happen.",
      "But right now there is also this moment.",
      "The room.",
      "The chair.",
      "The breath.",
      "My body here.",
    ]),
    mindfulness: localizedGenderNode(
      ["I return to what's around me and inside me right now."],
      ["I return to what's around me and inside me right now."]
    ),
    prompts: localizedGenderNode(
      [
        "What do I hear right now?",
        "What do I see around me?",
        "What do I feel in this very moment?",
      ],
      [
        "What do I hear right now?",
        "What do I see around me?",
        "What do I feel in this very moment?",
      ]
    ),
    closing: localizedGenderNode(
      ["This moment brings me back a little to solid ground."],
      ["This moment brings me back a little to solid ground."]
    ),
    action: localizedGenderNode(
      "Today I notice my feet on the floor for a moment.",
      "Today I notice my feet on the floor for a moment."
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "strength",
    title: "Before seeking certainty",
    opening: localizedGenderNode([
      "I have an urge to know right now.",
      "To check right now.",
      "To settle right now.",
      "But sometimes it's that very search",
      "that keeps the worry alive.",
    ]),
    mindfulness: localizedGenderNode([
      "I pause for a moment before I run to check.",
    ]),
    prompts: localizedGenderNode(
      [
        "What am I trying to find out right now?",
        "What would happen if I didn't know right away?",
        "What could help me wait a moment?",
      ],
      [
        "What am I trying to find out right now?",
        "What would happen if I didn't know right away?",
        "What could help me wait a moment?",
      ]
    ),
    closing: localizedGenderNode(
      ["I waited a moment.", "That already changes something."],
      ["I waited a moment.", "That already changes something."]
    ),
    action: localizedGenderNode(
      "Today I pause for one moment before one check.",
      "Today I pause for one moment before one check."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "strength",
    title: "One small step",
    opening: localizedGenderNode(
      [
        "I don't need to solve my whole life",
        "to feel a little better.",
        "I need one small step.",
        "One I can actually take.",
      ],
      [
        "I don't need to solve my whole life",
        "to feel a little better.",
        "I need one small step.",
        "One I can actually take.",
      ]
    ),
    mindfulness: localizedGenderNode(
      ["I feel what small step is possible for me today."],
      ["I feel what small step is possible for me today."]
    ),
    prompts: localizedGenderNode(
      [
        "What in me is ready to choose something small?",
        "When does it feel possible to me?",
        "What feels right to remember in a hard moment?",
      ],
      [
        "What in me is ready to choose something small?",
        "When does it feel possible to me?",
        "What feels right to remember in a hard moment?",
      ]
    ),
    closing: localizedGenderNode(
      ["I chose something small.", "That's enough for today."],
      ["I chose something small.", "That's enough for today."]
    ),
    action: localizedGenderNode(
      "Today I stay with the small step I chose.",
      "Today I stay with the small step I chose."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "A sentence to return to",
    opening: localizedGenderNode(
      [
        "The worry will come back sometimes.",
        "I know that.",
        "But I know it a little better now.",
        "And I don't have to go all the way with it each time.",
      ],
      [
        "The worry will come back sometimes.",
        "I know that.",
        "But I know it a little better now.",
        "And I don't have to go all the way with it each time.",
      ]
    ),
    mindfulness: localizedGenderNode(
      ["I breathe and come back to myself."],
      ["I breathe and come back to myself."]
    ),
    prompts: localizedGenderNode(
      [
        "What have I learned about this worry?",
        "What's the first sign that it's rising?",
        "What short sentence do I want to carry with me?",
      ],
      [
        "What have I learned about this worry?",
        "What's the first sign that it's rising?",
        "What short sentence do I want to carry with me?",
      ]
    ),
    closing: localizedGenderNode(
      ["I continue from here with a little more space."],
      ["I continue from here with a little more space."]
    ),
    action: localizedGenderNode(
      "Today I keep one short sentence that brings me back to myself.",
      "Today I keep one short sentence that brings me back to myself."
    ),
  },
].map(buildNormalizedDay);

export const anxietyGeneralBlueprintEn = buildNormalizedPilotBlueprint({
  id: "anxiety_general_14d",
  durationDays: 14,
  version: "normalizer_phase2_batch_01_v1",
  problemKey: "anxiety_general",
  category: "anxiety",
  journeyName: {
    he: "דאגנות יתר בכל נושא",
    en: "Excessive worry about everything",
    es: "Preocupación excesiva por todo",
  },
  days,
});

export default anxietyGeneralBlueprintEn;
